import { Checkbox, TextField as InputField, Fab, FormControlLabel, FormGroup, Grid, Hidden, InputBase, Paper, Theme, Typography, createStyles, makeStyles, withStyles } from "@material-ui/core";
import { Field, FieldProps, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import AddressPicker from "../../../../AddressPicker/AddressPicker";
import { getDefaultCountryCode, getLocalizedTaxSyntex } from "../../../../../utils/localized.syntex";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../../../../store";
import * as Yup from "yup";
import { useLazyQuery, useMutation } from "@apollo/client";
import { CREATE_CONSUMER_BUSINESS_CUSTOMER } from "../../../../../graphql/customers/createBusinessCustomerMutation";
import { useSnackBar } from "../../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../../SnackbarWrapper/SnackbarWrapper";
import { ApolloError } from "apollo-boost";
import { buildFieldRequiredMap, buildFieldVisibilityMap, defaultWebsiteBusinessFields, formatGraphQLErrorMessage, IBusinessRequireMap } from "../../../../utils";
import { BusinessCustomerStatus } from "../../../../../reducers/auth/types";
import { useTranslation } from "react-i18next";
import { GET_CUSTOM_FORM_BY_TYPE } from "../../../../../graphql/customers/getConsumerCustomForm";
import { CustomQuestionsFields } from "../../../../CustomQuestionsFields/CustomQuestionsFields";

const initialValues = {
  businessName: "",
  registrationNumber: "",
  VAT: "",
  registeredAddress: {
    city: "",
    country: "",
    fullAddress: "",
    state: "",
    street: "",
    zipcode: ""
  },
  tradingAddress: {
    city: "",
    country: "",
    fullAddress: "",
    state: "",
    street: "",
    zipcode: ""
  },
  billing: {
    accountNumber: "",
    email: "",
    phoneNumber: {
      country: "",
      phone: ""
    }
  },
  contact: {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: {
      phone: "",
      country: ""
    }
  },
  tradingAddressSame: false,
  approvedDrivers: [],
  authorizedSignatories: [],
  documents: [],
  overrideNetPaymentTerms: false,
  invoicingDay: 0,
  requiresBookingApproval: false,
  notes: [],
  netPaymentTerms: 0,
  overrideInvoiceDate: false,
  dailyBillingCycle: 0,
  weeklyBillingCycle: 0,
  monthlyBillingCycle: 0,
  status: BusinessCustomerStatus.PENDING,
  additionalQuestions: []
}

const StyledInput = withStyles(() =>
  createStyles({
    root: {
      border: "1px solid #c0c0c0",
      borderRadius: 6,
      padding: "8px 10px",
      marginTop: 5,
      '&.Mui-focused': {
        borderColor: "var(--primary-color)"
      },
      '&.Mui-error': {
        borderColor: "#D13135",
        color: "#D13135"
      }
    },
  }),
)(InputBase);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    checkboxLabel: {
      fontSize: "16px !important"
    },
    container: {
      backgroundColor: "#f4f4fa",
      display: "flex",
      flexDirection: "row",
      height: "100%"
    },
    content: {
      height: "auto",
      marginLeft: "50px",
      marginRight: "50px",
      marginTop: "100px"
    },
    root: {
      padding: theme.spacing(3)
    },
    section: {
      marginBottom: "25px"
    }
  })
);
interface IProps {
  onSubmit(): void
}

export interface ValidationContext {
  requiredMap: IBusinessRequireMap;
}
export const NewCustomerInfo = (props: IProps) => {
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const organisationData = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.organisation);
  const isMultiOrganisation = website.isMultiOrganisation || false;
  const { country } = organisationData.address;
  const snackbar = useSnackBar();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [values, setValues] = useState<any>(initialValues);
  const { t } = useTranslation();
  const [requiredMap, setRequiredMap] = useState<Record<string, boolean>>(
    buildFieldRequiredMap(defaultWebsiteBusinessFields)
  );
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>(
    buildFieldVisibilityMap(defaultWebsiteBusinessFields)
  );
  const [customQuestions, setCustomQuestions] = useState<any[]>([]);
  const validateCustomQuestions = (customQuestions: any[], answers: any[]) => {
    const requiredQuestions = customQuestions.filter(q => q.isRequired);
    const missingAnswers = requiredQuestions.filter(q => {
      const questionId = q.id || `q_${q.questionText}`;
      const answer = answers.find(a => a.questionId === questionId);
      return !answer || !answer.answer || answer.answer.trim() === '';
    });
    
    return missingAnswers;
  };

  const [createConsumerBusinessCustomer] = useMutation(CREATE_CONSUMER_BUSINESS_CUSTOMER, {
    onCompleted: () => {
      snackbar({
        message: t("snackBar:businessCustomerCreated"),
        variant: SnackBarVariant.SUCCESS
      });
      props.onSubmit()
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  const [getCustomFormByType] = useLazyQuery(GET_CUSTOM_FORM_BY_TYPE, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {

      if (data?.getConsumerCustomFormType?.config?.website?.fields.length) {
        const customMap = buildFieldRequiredMap(data.getConsumerCustomFormType.config.website.fields);
        const customVisibilityMap = buildFieldVisibilityMap(data.getConsumerCustomFormType.config.website.fields);
        setRequiredMap(customMap);
        setVisibilityMap(customVisibilityMap);
      } else {
        const defaultMap = buildFieldRequiredMap(defaultWebsiteBusinessFields);
        const defaultVisibilityMap = buildFieldVisibilityMap(defaultWebsiteBusinessFields);
        setRequiredMap(defaultMap);
        setVisibilityMap(defaultVisibilityMap);
      }
      const questions = data?.getConsumerCustomFormType?.config?.website?.customQuestions || [];
      setCustomQuestions(questions);
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
      const defaultMap = buildFieldRequiredMap(defaultWebsiteBusinessFields);
      const defaultVisibilityMap = buildFieldVisibilityMap(defaultWebsiteBusinessFields);
      setRequiredMap(defaultMap);
      setVisibilityMap(defaultVisibilityMap);
    }
  });

  useEffect(() => {
    const defaultCountryCode = getDefaultCountryCode(country);
    setValues((prevValues: any) => ({
      ...prevValues,
      billing: {
        ...prevValues.billing,
        phoneNumber: {
          ...prevValues.billing.phoneNumber,
          country: defaultCountryCode
        }
      },
      contact: {
        ...prevValues.contact,
        phoneNumber: {
          ...prevValues.contact.phoneNumber,
          country: defaultCountryCode
        }
      }
    }));
  }, [country]);

  useEffect(() => {
    getCustomFormByType({
      variables: {
        organisationId: organisationData.id,
        formType: "BUSINESS",
        atBranchLevel: false
      }
    })
  }, [])

  const getCountryFromLocaleString = (localeString: string) => {
    const alpha2 = localeString.slice(-2);
    if (alpha2) {
      return alpha2;
    }
    return "GB"
  }
  function handleCheckbox(
    event: React.ChangeEvent<HTMLInputElement>,
    data: any
  ) {
    if (event.target.checked) {
      setValues({
        ...values,
        tradingAddress: data,
        tradingAddressSame: true
      });
    } else {
      setValues({
        ...values,
        tradingAddressSame: false
      });
    }
  }

  const createDynamicValidationSchema = (requiredMap: IBusinessRequireMap, t: (key: string) => string) => {
    const createConditionalString = (fieldKey: keyof IBusinessRequireMap, errorKey: string) => {
      return requiredMap[fieldKey] ? Yup.string().required(t(errorKey)) : Yup.string()
    }

    const createConditionalEmail = (
      fieldKey: keyof IBusinessRequireMap,
      requiredErrorKey: string,
      invalidErrorKey: string,
    ) => {
      const baseSchema = Yup.string().email(t(invalidErrorKey))
      return requiredMap[fieldKey] ? baseSchema.required(t(requiredErrorKey)) : baseSchema
    }

    const createConditionalPhone = (fieldKey: keyof IBusinessRequireMap, errorKey: string) => {
      const baseSchema = Yup.string().test("is-valid-phone", t("root:invalidPhoneNumber"), (value) => {
        if (!value) return true
        return isValidPhoneNumber(value)
      })
      return requiredMap[fieldKey] ? baseSchema.required(t(errorKey)) : baseSchema
    }

    const createAddressSchema = (fieldKey: keyof IBusinessRequireMap, prefix: string) => {
      if (!requiredMap[fieldKey]) {
        return Yup.object().shape({
          fullAddress: Yup.string(),
          country: Yup.string(),
          state: Yup.string(),
          city: Yup.string(),
          street: Yup.string(),
          zipcode: Yup.string(),
        })
      }

      return Yup.object().shape({
        fullAddress: Yup.string().required(t(`validation:${prefix}Required`)),
        country: Yup.string().required(t(`validation:${prefix}CountryRequired`)),
        state: Yup.string().required(t(`validation:${prefix}StateRequired`)),
        city: Yup.string().required(t(`validation:${prefix}CityRequired`)),
        street: Yup.string().required(t(`validation:${prefix}StreetRequired`)),
        zipcode: Yup.string(),
      })
    }

    return Yup.object().shape({
      businessName: createConditionalString("businessName", "validation:businessNameRequired"),
      registrationNumber: createConditionalString("registrationNumber", "validation:registrationNumberRequired"),
      VAT: createConditionalString("VAT", "validation:vatRequired"),

      registeredAddress: createAddressSchema("registeredAddress", "registeredAddress"),
      tradingAddress: createAddressSchema("tradingAddress", "tradingAddress"),

      billing: Yup.object().shape({
        email: createConditionalEmail(
          "billingEmail",
          "validation:billingEmailRequired",
          "validation:billingEmailInvalid",
        ),
        accountNumber: createConditionalString("accountNumber", "validation:accountNumberRequired"),
        phoneNumber: Yup.object().shape({
          phone: createConditionalPhone("billingPhoneNumber", "validation:phoneNumberRequired"),
          country: Yup.string(),
        }),
      }),

      contact: Yup.object().shape({
        firstName: createConditionalString("contactFirstName", "validation:firstNameRequired"),
        lastName: createConditionalString("contactLastName", "validation:lastNameRequired"),
        email: createConditionalEmail("contactEmail", "validation:contactEmailRequired", "root:contactEmailInvalid"),
        phoneNumber: Yup.object().shape({
          phone: createConditionalPhone("contactPhoneNumber", "validation:phoneNumberRequired"),
          country: Yup.string(),
        }),
      }),
      orgCode: isMultiOrganisation ? 
        Yup.string().required(`${t('validation:organisationCodeRequired')}`)
        : Yup.string().nullable()
    })
  }

  const functionalSchema = createDynamicValidationSchema(requiredMap as any, t);

  const isVisible = (key: keyof IBusinessRequireMap) =>
    visibilityMap[key] !== false;

  const showRegistered = isVisible("registeredAddress");
  const showTrading = isVisible("tradingAddress");


  return (
    <div>
      <Paper elevation={0} className={classes.root}>
        <Formik
          enableReinitialize
          initialValues={values}
          validationSchema={functionalSchema}
          onSubmit={(values, { setSubmitting }) => {
            values.authorizedSignatories.push(values.contact);
            
            const missingRequired = validateCustomQuestions(customQuestions, values.additionalQuestions || []);
            if (missingRequired.length > 0) {
              snackbar({
                message: `Please answer required questions: ${missingRequired.map(q => q.questionText).join(', ')}`,
                variant: SnackBarVariant.ERROR
              });
              setSubmitting(false);
              return;
            }

            const submitValues = {
              ...values,
              additionalQuestions: values.additionalQuestions || []
            };
            createConsumerBusinessCustomer({
              variables: {
                businessCustomer: submitValues
              }
            })
            setSubmitting(false)
          }}
        >
          {(props) => (
            <Form noValidate style={{ margin: 'auto' }} >
              <Grid spacing={2} container>
                <Grid item container xs={12}>
                  <Typography variant="h2">{t('root:companyInformation')}</Typography>
                </Grid>
                {visibilityMap.businessName && (
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small">{t('root:businessName')}{requiredMap.businessName && <span>&nbsp;*</span>}</Typography>
                    <Field name="businessName">
                      {({ field, meta: { touched, error }, }: FieldProps) => (
                        <>
                          <StyledInput
                            {...field}
                            placeholder={t("root:enterBusinessName")}
                            fullWidth
                            error={touched && Boolean(error)}
                          />
                          <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                        </>
                      )}
                    </Field>
                  </Grid>
                )}
                {visibilityMap.registrationNumber && (
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small">{t('root:registrationNumber')}{requiredMap.registrationNumber && <span>&nbsp;*</span>}</Typography>
                    <Field name="registrationNumber">
                      {({ field, meta: { touched, error }, }: FieldProps) => (
                        <>
                          <StyledInput
                            {...field}
                            placeholder={t('root:registrationNumberPlaceholder')}
                            fullWidth
                            error={touched && Boolean(error)}
                          />
                          <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                        </>
                      )}
                    </Field>
                  </Grid>
                )}
                {visibilityMap.VAT && (
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small">{`${getLocalizedTaxSyntex(organisationData.locale)} ${t("root:registrationNum")}`}{requiredMap.VAT && <span>&nbsp;*</span>}</Typography>
                    <Field name="VAT">
                      {({ field, meta: { touched, error }, }: FieldProps) => (
                        <>
                          <StyledInput
                            {...field}
                            placeholder={`${getLocalizedTaxSyntex(organisationData.locale)} ${t("root:registrationNum")}`}
                            fullWidth
                            error={touched && Boolean(error)}
                          />
                          <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                        </>
                      )}
                    </Field>
                  </Grid>
                )}


                {(showRegistered || showTrading) && (
                  <Grid item container spacing={2}>

                    {showRegistered && (
                      <Grid item xs={12} sm={12} md={4}>
                        <Typography variant="h3">
                          {t('root:registeredAddressCaps')}
                        </Typography>
                      </Grid>
                    )}

                    {showTrading && (
                      <Hidden xsDown>
                        <Grid item xs={12} sm={12} md={4}>
                          <Typography variant="h3">
                            {t('root:tradingAddressCaps')}
                          </Typography>
                        </Grid>
                      </Hidden>
                    )}
                  </Grid>
                )}

                {(showRegistered || showTrading) && (
                  <Grid item container spacing={2}>

                    {showRegistered && (
                      <Grid item xs={12} sm={12} md={4}>
                        <Typography className="opacity60 padding-bottom--small">
                          {t('root:address')}
                        </Typography>
                        <AddressPicker
                          fieldName={"registeredAddress"}
                          required={requiredMap.registeredAddress}
                          onChange={(address: any) => {
                            if (address) {
                              props.setFieldValue("registeredAddress", address);
                              setValues({ ...props.values, registeredAddress: address });
                            }
                          }}
                        />
                      </Grid>
                    )}

                    {showTrading && (
                      <Hidden smUp>
                        <Grid item xs={12}>
                          <Typography variant="h3">
                            {t('root:tradingAddressCaps')}
                          </Typography>
                        </Grid>
                      </Hidden>
                    )}

                    {showTrading && (
                      <Grid item xs={12} sm={12} md={4}>
                        <Typography className="opacity60 padding-bottom--small">
                          {t('root:address')}
                        </Typography>

                        {!props.values.tradingAddressSame ? (
                          <AddressPicker
                            fieldName={"tradingAddress"}
                            required={requiredMap.tradingAddress}
                            onChange={(address: any) => {
                              if (address) {
                                props.setFieldValue("tradingAddress", address);
                                setValues({ ...props.values, tradingAddress: address });
                              }
                            }}
                          />
                        ) : (
                          <InputField
                            disabled
                            fullWidth
                            value={props.values.registeredAddress.fullAddress}
                          />
                        )}
                      </Grid>
                    )}

                    {showTrading && showRegistered &&  (
                      <Grid item xs={12} sm={12} md={4} style={{ display: 'flex', alignItems: 'center' }}>
                        <FormGroup row className={classes.checkboxLabel}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.tradingAddressSame}
                                onChange={(e) =>
                                  handleCheckbox(e, values.registeredAddress)
                                }
                                color="secondary"
                              />
                            }
                            label={
                              <Typography variant="body1">
                                {t('root:useRegisteredAddress')}
                              </Typography>
                            }
                          />
                        </FormGroup>
                      </Grid>
                    )}

                  </Grid>
                )}
                <Grid item container xs={12}>
                  <Grid item container xs={6}>
                    <Typography variant="h3" gutterBottom>
                      {t('root:billingInformationCaps')}
                    </Typography>
                  </Grid>
                </Grid>
                {visibilityMap.billingEmail && (
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small" >{t('root:billingEmailAddress')}{requiredMap.billingEmail && <span>&nbsp;*</span>} </Typography>
                    <Field name="billing.email">
                      {({ field, meta: { touched, error }, }: FieldProps) => (
                        <>
                          <StyledInput
                            {...field}
                            placeholder={t('root:billingEmailLabel')}
                            fullWidth
                            error={touched && Boolean(error)}
                          />
                          <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                        </>
                      )}
                    </Field>
                  </Grid>
                )}
                {visibilityMap.accountNumber && (
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small">
                      {t('root:accountNumber')}
                      {requiredMap.accountNumber && <span>&nbsp;*</span>}</Typography>
                    <Field name="billing.accountNumber">
                      {({ field, meta: { touched, error }, }: FieldProps) => (
                        <>
                          <StyledInput
                            {...field}
                            placeholder={t('root:accountNumber')}
                            fullWidth
                            required={requiredMap.accountNumber}
                            error={touched && Boolean(error)}
                          />
                          <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                        </>
                      )}
                    </Field>
                  </Grid>
                )}
                {visibilityMap.billingPhoneNumber && (
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small">{t("root:billingPhoneNo")}{requiredMap.billingPhoneNumber && <span>&nbsp;*</span>}</Typography>
                    <Field
                      component={StyledInput}
                      name={"billing.phoneNumber.phone"}
                      required={requiredMap.billingPhoneNumber}
                    >
                      {({ field, meta: { touched, error } }: FieldProps) => {
                        return (
                          <div className={`phone-input-container`} style={{ marginTop: 5 }}>
                            <div style={{ border: `1px solid ${(touched && Boolean(error)) ? '#D13135' : '#c0c0c0'}`, borderRadius: 6 }}>
                              <PhoneInput
                                {...field}
                                international={false}
                                defaultCountry={getCountryFromLocaleString(organisationData.locale)}
                                placeholder={`${t("root:phonePlaceholder")}${requiredMap.billingPhoneNumber ? " *" : ""}`}
                                className={(touched && Boolean(error)) ? "PhoneInputInputError" : ""}
                                name={"billing.phoneNumber.phone"}
                                onChange={(val) => {
                                  props.setFieldValue('billing.phoneNumber.phone', val)
                                }}
                                onCountryChange={(val) => {
                                  props.setFieldValue('billing.phoneNumber.country', val)
                                }}
                              />
                            </div>
                            <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                          </div>
                        );
                      }}
                    </Field>
                  </Grid>
                )}
                <Grid item container xs={12}>
                  <Typography variant="h3" gutterBottom>
                    {t('root:customerContactCaps')}
                  </Typography>
                </Grid>
                {visibilityMap.contactFirstName && (
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small">{t("root:firstName")}{requiredMap.contactFirstName && <span>&nbsp;*</span>}</Typography>
                    <Field name="contact.firstName">
                      {({ field, meta: { touched, error }, }: FieldProps) => (
                        <>
                          <StyledInput
                            {...field}
                            placeholder={t("root:lastNameLabel")}
                            fullWidth
                            error={touched && Boolean(error)}
                          />
                          <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                        </>
                      )}
                    </Field>
                  </Grid>
                )}
                {visibilityMap.contactLastName && (
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small">{t('root:lastName')}{requiredMap.contactLastName && <span>&nbsp;*</span>}</Typography>
                    <Field name="contact.lastName">
                      {({ field, meta: { touched, error }, }: FieldProps) => (
                        <>
                          <StyledInput
                            {...field}
                            placeholder={t("root:lastNameLabel")}
                            fullWidth
                            error={touched && Boolean(error)}
                          />
                          <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                        </>
                      )}
                    </Field>
                  </Grid>
                )}
                {visibilityMap.contactEmail && (
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small">{t('root:contactEmail')}{requiredMap.contactEmail && <span>&nbsp;*</span>}</Typography>
                    <Field name="contact.email">
                      {({ field, meta: { touched, error }, }: FieldProps) => (
                        <>
                          <StyledInput
                            {...field}
                            placeholder={t('root:emailLabel')}
                            fullWidth
                            error={touched && Boolean(error)}
                          />
                          <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                        </>
                      )}
                    </Field>
                  </Grid>
                )}
                {visibilityMap.contactPhoneNumber && (
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small">{t('root:contactPhone')}</Typography>
                    <Field
                      component={StyledInput}
                      name={"contact.phoneNumber.phone"}
                      required={requiredMap.contactPhoneNumber}
                    >
                      {({ field, meta: { touched, error } }: FieldProps) => {
                        return (
                          <div className={`phone-input-container`} style={{ marginTop: 5 }}>
                            <div style={{ border: `1px solid ${(touched && Boolean(error)) ? '#D13135' : '#c0c0c0'}`, borderRadius: 6 }}>
                              <PhoneInput
                                {...field}
                                international={false}
                                defaultCountry={getCountryFromLocaleString(organisationData.locale)}
                                placeholder={`${t("root:phonePlaceholder")}${requiredMap.contactPhoneNumber ? " *" : ""}`}
                                className={(touched && Boolean(error)) ? "PhoneInputInputError" : ""}
                                name={"contact.phoneNumber.phone"}
                                onChange={(val) => {
                                  props.setFieldValue("contact.phoneNumber.phone", val)
                                }}
                                onCountryChange={(val) => {
                                  props.setFieldValue("contact.phoneNumber.country", val)
                                }}
                              />
                            </div>
                            <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                          </div>
                        );
                      }}
                    </Field>
                  </Grid>
                )}
                {isMultiOrganisation && <Grid item xs={12}>
                  <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                    <Typography variant="body1" className="opacity60 padding-bottom--small">{'Organisation Code'}{<span>&nbsp;*</span>}</Typography>
                    <Field name="orgCode">
                      {({ field, meta: { touched, error }, }: FieldProps) => (
                        <>
                          <StyledInput
                            {...field}
                            placeholder={'ABC123'}
                            fullWidth
                            error={touched && Boolean(error)}
                          />
                          <Typography variant="body2" color="error">{touched && Boolean(error) && error}</Typography>
                        </>
                      )}
                    </Field>
                  </Grid>
                </Grid>}
                {customQuestions.length > 0 && (
                  <Grid item xs={12}>
                    <CustomQuestionsFields
                      customQuestions={customQuestions}
                      answers={props.values.additionalQuestions || []}
                      onChange={(answers: any) => props.setFieldValue("additionalQuestions", answers)}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Fab
                    variant="extended"
                    size="medium"
                    aria-label="add"
                    type="submit"
                  >
                    {t("misc:create")}
                  </Fab>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </div>
  )
}